import { z } from 'zod';
// Monday Recap (s212) — the agency-feel anchor card.
// Spec §6 — "Monday 9am client-tz · The Recap 🐾":
//   - weekend numbers (spend, conversions, deltas vs prior week)
//   - what Mag is doing this week (3–5 bullets max)
//   - anything needing founder eyes — batched into one message
//   - one sentence of texture
//
// Shape choices here are constraints on the agent: the schema
// rejects too many bullets, missing texture, etc. Better to fail
// fast than ship a card that doesn't feel like the agency.
/** A single week-over-week metric tile rendered at the top of the
 *  recap. Both `current` and `prior` are nullable when the data
 *  source is cold (no metrics history yet); the UI renders "—" for
 *  null and skips the delta. */
export const recapMetricSchema = z.object({
    /** Display label, lowercase ("spend", "conversions", "ctr"). */
    label: z.string().min(1).max(40),
    /** Pre-formatted display value the dashboard renders verbatim
     *  ("$1,243.50", "47", "3.4%"). Pre-formatting in the agent path
     *  means the UI doesn't carry currency/number-formatting logic
     *  per metric type. */
    currentDisplay: z.string().min(1).max(40),
    /** Same for the prior period. Null on first-week brands. */
    priorDisplay: z.string().max(40).nullable(),
    /** Sign + magnitude as a number; the UI renders the arrow + color.
     *  Null when there's no prior to compare to. */
    deltaPercent: z.number().nullable(),
});
/** A single "open ask" reference. The recap doesn't repeat the full
 *  card content (the founder can click through to /home); it lists
 *  what needs attention so the recap reads as a true batch. */
export const recapOpenAskSchema = z.object({
    /** Feed card kind so the UI renders the right eyebrow color. */
    cardKind: z.string().min(1).max(40),
    title: z.string().min(1).max(200),
    /** ISO timestamp the card was created. UI renders "3d ago" etc. */
    createdAt: z.string(),
});
export const mondayRecapPayloadSchema = z.object({
    kind: z.literal('monday_recap'),
    /** ISO date of the Monday this recap covers (in brand tz). The
     *  scheduler dedups against this — at most one recap per Monday
     *  per brand. */
    weekStartDate: z.string(),
    /** Brand's tz the schedule fired in. Stored so the dashboard
     *  renders "for week of Mon dd in tz" without re-deriving. */
    tz: z.string().min(1).max(60),
    /** Up to 5 stat tiles. Order is meaningful — the agent puts the
     *  most-important metric first. Empty on cold brands (no paid
     *  metrics yet); the recap still lands with bullets + texture. */
    metrics: z.array(recapMetricSchema).max(5),
    /** What Mag is doing this week. 3–5 bullets, Mag-voice. Lowercase,
     *  "i", short. The agent's bullets render verbatim. */
    thisWeek: z.array(z.string().min(1).max(280)).min(1).max(5),
    /** One sentence of "texture" — a notable thing from the past week
     *  ("saturday's spike came from the bfcm teaser hook"). Mag voice;
     *  always present. */
    texture: z.string().min(1).max(280),
    /** Open feed cards the founder should look at. Capped at 6 — past
     *  that the recap starts feeling like a backlog dump. */
    openAsks: z.array(recapOpenAskSchema).max(6),
});
/** What the agent returns. Same shape as the payload minus the
 *  scheduler-set fields (weekStartDate, tz, metrics, openAsks —
 *  those are pre-aggregated and passed AS context, then included
 *  in the final card). The agent only authors `thisWeek` + `texture`. */
export const mondayRecapAgentOutputSchema = z.object({
    thisWeek: z.array(z.string().min(1).max(280)).min(1).max(5),
    texture: z.string().min(1).max(280),
});
//# sourceMappingURL=monday-recap.js.map